import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiExternalLink, FiGithub, FiFilter, FiLayers } from 'react-icons/fi'
import { Helmet } from 'react-helmet-async'
import api from '../utils/api.js'

const Projects = () => {
  const [projects, setProjects] = useState([])
  const [filteredProjects, setFilteredProjects] = useState([])
  const [activeFilter, setActiveFilter] = useState('all')
  const [loading, setLoading] = useState(true)

  const filters = ['all', 'react', 'node.js', 'python', 'full-stack']

  useEffect(() => {
    fetchProjects()
  }, [])

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects)
    } else {
      setFilteredProjects(
        projects.filter(project =>
          project.technologies.some(tech =>
            tech.toLowerCase().includes(activeFilter.toLowerCase())
          )
        )
      )
    }
  }, [projects, activeFilter])

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/projects')
      setProjects(response.data)
    } catch (error) {
      console.error('Error fetching projects:', error)
      // Use mock data for demo
      setProjects(mockProjects)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Projects - Portfolio</title>
        <meta name="description" content="Browse my portfolio of web development projects" />
      </Helmet>

      <div className="pt-20 section-padding bg-dark-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900 dark:text-gray-100">
              My Projects
            </h1>
            <p className="text-xl text-center mb-12 max-w-2xl mx-auto text-gray-600 dark:text-gray-300">
              A collection of my work showcasing different technologies and approaches to problem-solving.
            </p>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-4 justify-center items-center mb-12">
              <FiFilter className="text-2xl text-gray-500 dark:text-gray-400" />
              {filters.map((filter) => (
                <button
                  key={filter}
                  className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>

            {/* Projects Grid */}
            <motion.div
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              layout
            >
              {filteredProjects.map((project, index) => (
                <ProjectCard key={project._id || index} project={project} index={index} />
              ))}
            </motion.div>

            {filteredProjects.length === 0 && (
              <div className="text-center py-12">
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  No projects found for the selected filter.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  )
}

const ProjectCard = ({ project, index }) => {
  const backendUrl = import.meta.env.VITE_API_URL;
  const imageUrl = project.image && project.image.startsWith('http')
    ? project.image
    : `${backendUrl}${project.image}`;

  return (
  <motion.div
    className="glass-panel rounded-2xl p-6 shadow-2xl transition-all duration-700 hover:shadow-secondary_accent/10 hover:-translate-y-3 overflow-hidden relative group"
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
    layout
  >
    {/* Animated background glow */}
    <div className="absolute -inset-0.5 bg-gradient-to-r from-secondary_accent/20 to-accent/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>

    {/* Background Icon Decor */}
    <div className="absolute -right-6 -bottom-6 opacity-[0.05] group-hover:opacity-[0.1] transition-all duration-700 group-hover:scale-125 group-hover:-rotate-12 pointer-events-none z-0">
      <FiLayers size={180} />
    </div>

    <div className="aspect-video mb-6 rounded-xl overflow-hidden relative z-10 border border-white/5">
      <img
        src={imageUrl}
        alt={project.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
        <span className="text-white text-xs font-medium tracking-widest uppercase">View Details</span>
      </div>
    </div>
    
    <div className="relative z-10">
      <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-secondary_accent transition-colors tracking-tight">{project.title}</h3>
      <p className="text-gray-400 mb-6 line-clamp-3 text-sm leading-relaxed font-light">{project.description}</p>
      
      <div className="flex flex-wrap gap-2 mb-8">
        {project.technologies.map((tech) => (
          <span
            key={tech}
            className="text-[10px] uppercase tracking-tighter font-bold px-3 py-1 rounded-full bg-white/5 text-gray-400 border border-white/10 group-hover:border-secondary_accent/40 group-hover:text-gray-200 transition-all duration-300"
          >
            {tech}
          </span>
        ))}
      </div>
      
      <div className="flex gap-4">
        {project.liveUrl && (
          <a
            href={project.liveUrl}
            className="btn-primary flex-1 inline-flex items-center justify-center text-xs py-2.5 px-4 rounded-xl shadow-lg shadow-secondary_accent/10 hover:shadow-secondary_accent/30 transition-all duration-300"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiExternalLink className="mr-2" /> Live Demo
          </a>
        )}
        {project.githubUrl && (
          <a
            href={project.githubUrl}
            className="btn-secondary flex-1 inline-flex items-center justify-center text-xs py-2.5 px-4 rounded-xl border-white/10 hover:border-secondary_accent transition-all duration-300"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiGithub className="mr-2" /> Code
          </a>
        )}
      </div>
    </div>
  </motion.div>
  )
};

// Mock data for demo
const mockProjects = [
  {
    _id: '1',
    title: 'E-Commerce Platform',
    description: 'A full-stack e-commerce solution with React, Node.js, and MongoDB. Features include user authentication, payment processing, and admin dashboard.',
    image: 'https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg?auto=compress&cs=tinysrgb&w=800',
    technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
    liveUrl: '#',
    githubUrl: '#'
  },
  {
    _id: '2',
    title: 'Task Management App',
    description: 'A collaborative task management application with real-time updates, drag-and-drop functionality, and team collaboration features.',
    image: 'https://images.pexels.com/photos/7688460/pexels-photo-7688460.jpeg?auto=compress&cs=tinysrgb&w=800',
    technologies: ['React', 'Express', 'Socket.io', 'PostgreSQL'],
    liveUrl: '#',
    githubUrl: '#'
  },
  {
    _id: '3',
    title: 'Weather Dashboard',
    description: 'A responsive weather dashboard with location-based forecasts, interactive maps, and weather alerts.',
    image: 'https://images.pexels.com/photos/1118873/pexels-photo-1118873.jpeg?auto=compress&cs=tinysrgb&w=800',
    technologies: ['Vue.js', 'Python', 'Flask', 'OpenWeather API'],
    liveUrl: '#',
    githubUrl: '#'
  },
  {
    _id: '4',
    title: 'Social Media Analytics',
    description: 'A comprehensive analytics dashboard for social media performance tracking with data visualization and reporting.',
    image: 'https://images.pexels.com/photos/265087/pexels-photo-265087.jpeg?auto=compress&cs=tinysrgb&w=800',
    technologies: ['React', 'D3.js', 'Node.js', 'MongoDB'],
    liveUrl: '#',
    githubUrl: '#'
  }
]

export default Projects